import { useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Upload, FileSpreadsheet, Loader2, AlertTriangle, CheckCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

interface BulkImportDialogProps {
  onSuccess: () => void;
}

interface ParsedChild {
  full_name: string;
  date_of_birth?: string;
  parent_name?: string;
  parent_phone?: string;
  health_notes?: string;
  valid: boolean;
  error?: string;
}

const BulkImportDialog = ({ onSuccess }: BulkImportDialogProps) => {
  const { profile } = useAuth();
  const [open, setOpen] = useState(false);
  const [parsedData, setParsedData] = useState<ParsedChild[]>([]);
  const [importing, setImporting] = useState(false);
  const [fileName, setFileName] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);

  const parseCSV = (text: string): ParsedChild[] => {
    const lines = text.split(/\r?\n/).filter(l => l.trim());
    if (lines.length < 2) return [];

    const headers = lines[0].split(",").map(h => h.trim().replace(/"/g, ""));
    const nameIdx = headers.findIndex(h => /اسم.*طفل|full_name|name|الاسم/i.test(h));
    const dobIdx = headers.findIndex(h => /تاريخ.*ميلاد|date_of_birth|dob|الميلاد/i.test(h));
    const parentIdx = headers.findIndex(h => /ولي.*أمر|parent_name|parent|الأب|الأم/i.test(h));
    const phoneIdx = headers.findIndex(h => /هاتف|phone|جوال|رقم/i.test(h));
    const healthIdx = headers.findIndex(h => /صح|health|ملاحظ/i.test(h));

    if (nameIdx === -1) return [];

    return lines.slice(1).map(line => {
      const cols = line.split(",").map(c => c.trim().replace(/"/g, ""));
      const name = cols[nameIdx] || "";
      const valid = name.length >= 2;
      return {
        full_name: name,
        date_of_birth: dobIdx >= 0 ? cols[dobIdx] || undefined : undefined,
        parent_name: parentIdx >= 0 ? cols[parentIdx] || undefined : undefined,
        parent_phone: phoneIdx >= 0 ? cols[phoneIdx] || undefined : undefined,
        health_notes: healthIdx >= 0 ? cols[healthIdx] || undefined : undefined,
        valid,
        error: !valid ? "اسم الطفل مطلوب (حرفين على الأقل)" : undefined,
      };
    });
  };

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result as string;
      setParsedData(parseCSV(text));
    };
    reader.readAsText(file, "UTF-8");
  };

  const handleImport = async () => {
    if (!profile?.nursery_id) return;
    const validItems = parsedData.filter(d => d.valid);
    if (validItems.length === 0) {
      toast.error("لا توجد بيانات صالحة للاستيراد");
      return;
    }

    setImporting(true);
    const rows = validItems.map(d => ({
      nursery_id: profile.nursery_id!,
      full_name: d.full_name,
      date_of_birth: d.date_of_birth || null,
      parent_name: d.parent_name || null,
      parent_phone: d.parent_phone || null,
      health_notes: d.health_notes || null,
    }));

    const { error } = await supabase.from("children").insert(rows);
    setImporting(false);

    if (error) {
      toast.error("خطأ في الاستيراد: " + error.message);
    } else {
      toast.success(`تم استيراد ${validItems.length} طفل بنجاح`);
      setOpen(false);
      setParsedData([]);
      setFileName("");
      onSuccess();
    }
  };

  const validCount = parsedData.filter(d => d.valid).length;
  const invalidCount = parsedData.filter(d => !d.valid).length;

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setParsedData([]); setFileName(""); } }}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Upload className="w-4 h-4 ml-2" />
          استيراد
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>استيراد أطفال من ملف CSV</DialogTitle>
        </DialogHeader>

        {parsedData.length === 0 ? (
          <div className="space-y-4">
            <div
              className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-primary/50 transition-colors"
              onClick={() => fileRef.current?.click()}
            >
              <FileSpreadsheet className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
              <p className="text-sm text-muted-foreground">اضغط لاختيار ملف CSV</p>
              <p className="text-xs text-muted-foreground mt-1">
                الأعمدة المطلوبة: اسم الطفل | تاريخ الميلاد | ولي الأمر | الهاتف | ملاحظات صحية
              </p>
            </div>
            <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={handleFile} />
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">{fileName}</span>
              <div className="flex gap-2">
                <Badge variant="outline" className="text-emerald-600">
                  <CheckCircle className="w-3 h-3 ml-1" />{validCount} صالح
                </Badge>
                {invalidCount > 0 && (
                  <Badge variant="outline" className="text-destructive">
                    <AlertTriangle className="w-3 h-3 ml-1" />{invalidCount} خطأ
                  </Badge>
                )}
              </div>
            </div>

            <div className="max-h-60 overflow-y-auto border rounded-lg">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-muted/50 border-b">
                    <th className="p-2 text-right">الحالة</th>
                    <th className="p-2 text-right">اسم الطفل</th>
                    <th className="p-2 text-right">ولي الأمر</th>
                    <th className="p-2 text-right">الهاتف</th>
                  </tr>
                </thead>
                <tbody>
                  {parsedData.map((d, i) => (
                    <tr key={i} className={`border-b ${!d.valid ? "bg-destructive/5" : ""}`}>
                      <td className="p-2">
                        {d.valid ? (
                          <CheckCircle className="w-3 h-3 text-emerald-500" />
                        ) : (
                          <AlertTriangle className="w-3 h-3 text-destructive" />
                        )}
                      </td>
                      <td className="p-2">{d.full_name || "-"}</td>
                      <td className="p-2">{d.parent_name || "-"}</td>
                      <td className="p-2" dir="ltr">{d.parent_phone || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {parsedData.length > 0 && (
          <DialogFooter>
            <Button variant="outline" onClick={() => { setParsedData([]); setFileName(""); }}>
              إعادة اختيار
            </Button>
            <Button onClick={handleImport} disabled={importing || validCount === 0}>
              {importing && <Loader2 className="w-4 h-4 ml-2 animate-spin" />}
              استيراد {validCount} طفل
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BulkImportDialog;
